import React, { FC } from 'react';
import { AddLiquiditySectionTitle } from '@app/screens/liquidity/AddLiquidityV3/components/AddLiquiditySectionTitle';
import { Box, Button, Checkbox, HStack, Input, Text } from '@chakra-ui/react';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { formatNumber } from '@app/helpers/format';
import { useLockDetails } from '@app/hooks/plugins/useLockDetails';
import { InfoRow } from '@app/screens/liquidity/AddLiquidityV3/components/InfoRow';
import { useIsActivePool } from '@app/hooks/plugins/useIsActivePool';

import s from './LockSection.module.scss';
import {
  IDerivedLiquidityByIchiInfo,
  useLiquidityByIchiState
} from '@app/state/liquidityByIchiStore';

interface Props {
  mintInfo: IDerivedLiquidityByIchiInfo;
}

export const LockSection: FC<Props> = ({ mintInfo }) => {
  const {
    stakeToNitro,
    lockDuration,
    actions: { setStakeToNitro, setLockDuration }
  } = useLiquidityByIchiState();

  const { lockBonus, bonusMultiplier } = useLockDetails(
    mintInfo.nftPoolAddress,
    mintInfo.lockDuration
  );

  const { data: isActivePool } = useIsActivePool(mintInfo.nftPoolAddress);

  if (!isActivePool) {
    return null;
  }

  return (
    <>
      <AddLiquiditySectionTitle
        isCompleted={false}
        label="Boost options"
        hideStatus
      />
      <HStack
        justifyContent="space-between"
        mb="8px"
        bg="neutral.1000"
        borderRadius="12px"
        p="8px 14px"
      >
        <Text
          fontSize="14px"
          fontWeight={400}
          lineHeight="18px"
          whiteSpace="nowrap"
          flex={1}
        >
          Lock duration (days)
        </Text>
        <HStack
          sx={{
            paddingInline: '8px',
            paddingBLock: '4px',
            borderRadius: '10px',
            borderColor: 'gray.5',
            background: 'black',
            flex: [1, 0.5]
          }}
        >
          <Button
            variant="rowCompact"
            sx={{ minWidth: '36px' }}
            onClick={() => {
              setLockDuration(lockDuration - 1 < 0 ? 0 : lockDuration - 1);
            }}
          >
            <FaMinus />
          </Button>
          <Input
            variant="filled"
            borderRadius={4}
            w="100%"
            bg="transparent"
            fontSize="24px"
            fontWeight="400"
            lineHeight="1"
            letterSpacing="-0.96px"
            color={'white'}
            _focusVisible={{
              outlineColor: 'transparent',
              outlineWidth: '2px',
              outlineOffset: '2px',
              outlineStyle: 'dotted'
            }}
            _hover={{ bg: 'transparent' }}
            px={0}
            textAlign="right"
            type={'number'}
            formNoValidate
            lang="en"
            step={1}
            min={0}
            value={Number(lockDuration).toString()}
            onChange={e => {
              const val = e.target.value;

              if (+val > 183) {
                setLockDuration(183);
              } else if (+val < 0) {
                setLockDuration(0);
              } else {
                let noLeadingZeros = val.replace(/^0+/, '');

                setLockDuration(+noLeadingZeros);
              }
            }}
            placeholder="0"
            _placeholder={{ color: 'neutral.400' }}
            sx={{
              padding: '6px 14px',
              fontSize: '16px'
            }}
          />
          <Button
            variant="rowCompact"
            sx={{ minWidth: '36px' }}
            onClick={() => {
              setLockDuration(lockDuration + 1 > 183 ? 183 : lockDuration + 1);
            }}
          >
            <FaPlus />
          </Button>
        </HStack>
      </HStack>
      <Box p="0 14px">
        <InfoRow
          label={
            <>
              <Button
                color="orange"
                onClick={() => {
                  setLockDuration(183);
                }}
              >
                Set max bonus
              </Button>
            </>
          }
          value={`${formatNumber(+lockBonus)}% lock bonus (x${formatNumber(
            +bonusMultiplier
          )})`}
        />
      </Box>

      {mintInfo.isNitroPool && (
        <Box w="100%" p="8px 14px" display="none">
          <Text
            fontSize="14px"
            fontWeight={400}
            lineHeight="18px"
            whiteSpace="nowrap"
            flex={1}
            color="white"
            pb={3}
          >
            Nitro auto-staking
          </Text>
          <Checkbox
            size="md"
            colorScheme="green"
            className={s.checkbox}
            isChecked={stakeToNitro}
            onChange={() => setStakeToNitro(!stakeToNitro)}
          >
            Auto deposit your kpNFT into a nitro pool
          </Checkbox>
        </Box>
      )}
    </>
  );
};
